import React, { useState, ChangeEvent } from 'react';
import axios from 'axios';
import { Alert, Col, Row } from 'reactstrap';
import Swal from 'sweetalert2';
import { environment } from '../../../environment/environment';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import EmployeeRiskAuditTrail from './EmployeeRiskAuditTrail';
import { EmployeeData, RiskProfile } from './types';

interface EmployeeRiskProfileFormProps {
  employeeData: EmployeeData;
}

const EmployeeRiskProfileForm: React.FC<EmployeeRiskProfileFormProps> = ({
  employeeData,
}) => {
  const fieldWidth = 7;
  const labelWidth = 5;

  const [riskProfile, setRiskProfile] = useState<RiskProfile>({
    risky: employeeData.risky || false,
    risky_reason: employeeData.risky_reasons?.reasons?.join(', ') || '',
  });

  const [initialRiskProfile, setInitialRiskProfile] = useState<RiskProfile>({
    risky: employeeData.risky || false,
    risky_reason: employeeData.risky_reasons?.reasons?.join(', ') || '',
  });

  const [auditTrailRefreshTrigger, setAuditTrailRefreshTrigger] = useState(0);

  const handleRiskProfileChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;
    const {checked} = (event.target as HTMLInputElement);

    setRiskProfile({
      ...riskProfile,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const hasRiskProfileChanges = (): boolean => (
      riskProfile.risky !== initialRiskProfile.risky ||
      riskProfile.risky_reason !== initialRiskProfile.risky_reason
    );

  const saveRiskProfile = () => {
    const reason = riskProfile.risky && riskProfile.risky_reason
      ? riskProfile.risky_reason.trim()
      : null;

    const payload = {
      employee: employeeData.id,
      risky: riskProfile.risky,
      ...(reason && { risky_reason: reason }),
    };

    const url = `${environment.baseUrl}/employee/update-risky/`;

    axios
      .post(url, payload)
      .then(() => {
        Swal.fire({
          title: 'Risk profile updated',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        setInitialRiskProfile({ ...riskProfile });
        setAuditTrailRefreshTrigger((prev) => prev + 1);
      })
      .catch((error) => {
        apiResponseHandler.handleApiError('Error', error);
      });
  };

  return (
    <div style={{ maxWidth: '670px', margin: 'auto' }}>
      <Alert
        className="mb-4"
        style={{
          backgroundColor: '#fffbea',
          borderColor: '#f5e6a8',
          color: '#856404',
        }}
      >
        When a user is marked as risky, their chatbot session will be reset.
        Their next session will have restricted access to all sensitive features including EWA, VAS, Jem Mobile, Payslips and IRP5s.
      </Alert>

      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label
            htmlFor="riskyCheckbox"
            style={{
              fontWeight: '800',
              marginBottom: 0,
              lineHeight: '1.5rem',
              display: 'inline-block',
              paddingTop: '0.375rem',
            }}
          >
            Risky
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <input
            type="checkbox"
            id="riskyCheckbox"
            name="risky"
            checked={riskProfile.risky}
            onChange={handleRiskProfileChange}
            style={{
              marginTop: '0.5rem',
              width: '18px',
              height: '18px',
              cursor: 'pointer',
            }}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg={labelWidth}>
          <label style={{ fontWeight: '800' }} htmlFor="risky_reason">
            Reason
          </label>
        </Col>
        <Col lg={fieldWidth}>
          <textarea
            className="form-control"
            id="risky_reason"
            name="risky_reason"
            rows={4}
            maxLength={200}
            value={riskProfile.risky_reason}
            onChange={handleRiskProfileChange}
            disabled={!riskProfile.risky}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <button
            disabled={!hasRiskProfileChanges()}
            onClick={saveRiskProfile}
            className="btn btn-primary btn-block"
          >
            Save Risk Profile
          </button>
        </Col>
      </Row>

      <EmployeeRiskAuditTrail
        employeeId={employeeData.id}
        refreshTrigger={auditTrailRefreshTrigger}
      />
    </div>
  );
};

export default EmployeeRiskProfileForm;
